import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { ArrowLeft, Loader2, Calendar } from 'lucide-react';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import gqlTag from 'graphql-tag';
import { format } from 'date-fns';
import * as utils from '../../../application/common/Utilities';
import {
  blankIfValue,
  defaultDateFormat,
  graphqlDateFormat,
  LOG,
} from '../../../application/common/Utilities';
import { createReward } from '../../../graphql/mutations';
import * as mutations from '../../../graphql/mutations';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FormField } from '../../components/ui/form-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ConfirmModal } from '../../components/ui/confirm-modal';
import { useToast } from '../../components/ui/toast';
import { cn } from '../../lib/utils';

const gql: any = gqlTag;

/**
 * A date input that shows a calendar icon instead of the native browser icon.
 */
function DateInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="pr-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
      />
      <Calendar className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
    </div>
  );
}

declare const global: {
  me: {
    id: string;
    orgID: string;
    isAdmin: boolean;
    imageName?: string;
    name: string;
  };
  appSettings: {
    labels: any;
    dateFormat: string;
  };
};

const createRewardValidationSchema = () =>
  yup.object().shape({
    cost: yup
      .number()
      .required()
      .positive()
      .integer()
      .min(0, 'Value must be at least 0')
      .max(1000000, 'Value can be no larger than 1000000')
      .label('Reward Cost'),
    limitCount: yup
      .mixed()
      .required()
      .test('limitCount-validation', 'Invalid limit count', function (value) {
        if (value === '999999') {
          return true;
        }
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 100000) {
          return false;
        }
        return true;
      })
      .label('Limit'),
    personLimit: yup
      .mixed()
      .required()
      .test('personLimit-validation', 'Invalid person limit', function (value) {
        if (value === '999999') {
          return true;
        }
        const numValue = Number(value);
        if (isNaN(numValue) || numValue < 1 || numValue > 1000) {
          return false;
        }
        return true;
      })
      .label('Limit'),
    name: yup
      .string()
      .label('Name')
      .required()
      .min(5, 'The name is too short. Please enter at least 5 characters'),
    tags: yup
      .string()
      .label('Tags')
      .min(5, 'The tag name is too short. Please enter at least 5 characters')
      .max(32, 'The tag name is too long.'),
    validFrom: yup
      .string()
      .required()
      .label('Start date')
      .matches(
        utils.dateRegExp,
        'Please enter a valid date in the form ' +
          (global.appSettings?.dateFormat || 'MM/dd/yyyy')
      ),
    validTo: yup
      .string()
      .required()
      .label('End date')
      .test('validTo-validation', 'Invalid end date', function (value) {
        if (value === '01/01/2100') {
          return true;
        }
        if (!value || !utils.dateRegExp.test(value)) {
          return false;
        }
        const { validFrom } = this.parent;
        return new Date(value) >= new Date(validFrom);
      }),
  });

function getNewRewardDefaults() {
  return {
    id: '',
    name: '',
    description: '',
    rewardType: 'no_line',
    personLimit: '1',
    limitCount: '1',
    value: '',
    valueType: '',
    remainingCount: '',
    tags: '',
    validFrom: format(new Date(), 'MM/dd/yyyy'),
    validTo: format(
      new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      'MM/dd/yyyy'
    ),
    launchDate: '',
    isActive: 1,
    cost: '100',
    costType: '',
    costName: 'points',
    ownerID: global.me.orgID,
    ownerType: global.me.isAdmin ? 'public' : 'circle',
    approverType: 'auto',
    location: '',
    uploadloadingPicture: false,
    uploadPictureTitle: 'Upload Picture / Video',
    imageName: '',
    imageType: '',
    orgID: global.me.orgID,
    useRewardCatalog: false,
    cloneReward: false,
    utid: '',
    tangoAccountID: '',
  };
}

export function RewardFormPage() {
  const navigate = useNavigate();
  const { rewardId } = useParams();
  const toast = useToast();

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const isEditMode = Boolean(rewardId);

  const [initialValues, setInitialValues] = useState<any>(() => {
    return isEditMode ? {} : getNewRewardDefaults();
  });

  async function getRewardForScreen(rewardID: string) {
    if (rewardID === '' || rewardID === undefined) {
      return initialValues;
    }

    const myQuery = gql`
      query getRewardDetails($rewardID: ID!) {
        getReward(id: $rewardID) {
          id
          isDeleted
          approverType
          cost
          costType
          description
          imageName
          imageType
          isActive
          limitCount
          location
          name
          personLimit
          remainingCount
          rewardType
          validFrom
          validTo
          value
          valueType
          ownerType
          utid
          tangoAccountID
        }
      }
    `;

    const rewardData: any = await API.graphql({
      query: myQuery,
      variables: { rewardID },
    });

    if (!rewardData.data.getReward) {
      const defaults = getNewRewardDefaults();
      setInitialValues(defaults);
      return defaults;
    }

    const reward = rewardData.data.getReward;
    const ourReward = {
      id: reward.id,
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType,
      personLimit: reward.personLimit.toString(),
      limitCount: reward.limitCount.toString(),
      value: reward.value.toString(),
      valueType: reward.valueType,
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: defaultDateFormat(
        blankIfValue(reward.validFrom, utils.MIN_DATE)
      ),
      validTo:
        reward.validTo === '2100-01-01'
          ? '01/01/2100'
          : defaultDateFormat(blankIfValue(reward.validTo, utils.MAX_DATE)),
      launchDate: defaultDateFormat(
        blankIfValue(reward.launchDate, utils.MAX_DATE)
      ),
      isActive: reward.isActive,
      cost: reward.cost.toString(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      orgID: reward.orgID,
      remainingCount: reward.remainingCount,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      uploadPictureTitle: 'Upload Picture / Video',
      useRewardCatalog: false,
      cloneReward: false,
      utid: reward.utid,
      tangoAccountID:
        reward.tangoAccountID !== 'NA' ? reward.tangoAccountID : '',
    };

    setInitialValues(ourReward);

    if (reward.imageName && reward.imageName !== '') {
      if (
        reward.imageType === 'gif_url' ||
        reward.imageType === 'giftcard_url' ||
        reward.imageType === 'trophy'
      ) {
        setImagePreviewUrl(reward.imageName);
      } else {
        const url = await Storage.get(reward.imageName);
        setImagePreviewUrl(url as string);
      }
    }
  }

  async function updateRewardRecord(reward: any) {
    if (reward.ownerType === 'public') {
      reward.ownerID = global.me.orgID;
    }
    const ourReward = {
      id: reward.id,
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType,
      personLimit: reward.personLimit.toString().trim(),
      limitCount: reward.limitCount.toString().trim(),
      value: reward.value.toString().trim(),
      valueType: reward.valueType,
      remainingCount: reward.remainingCount.toString().trim(),
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: graphqlDateFormat(reward.validFrom),
      validTo: graphqlDateFormat(reward.validTo),
      launchDate: graphqlDateFormat(
        utils.defaultIfEmpty(reward.launchDate, reward.validFrom)
      ),
      isActive: reward.isActive,
      cost: reward.cost.toString().trim(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      orgID: reward.orgID,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      isDeleted: false,
      utid: reward.utid,
      tangoAccountID:
        reward.tangoAccountID !== '' ? reward.tangoAccountID : 'NA',
    };

    try {
      await API.graphql({
        query: mutations.updateReward,
        variables: { input: ourReward },
      });
      toast.success(
        'Reward Updated',
        'Your reward has been successfully updated'
      );
      return true;
    } catch (err) {
      toast.error('Update Failed', 'There was an error updating your reward');
      LOG.error('updateReward(): error updating Reward:' + err);
      return false;
    }
  }

  async function createRewardRecord(reward: any) {
    if (reward.id !== '') {
      return await updateRewardRecord(reward);
    }

    if (reward.ownerType === 'public') {
      reward.ownerID = global.me.orgID;
      reward.ownerName = 'Everyone';
    }

    const ourReward = {
      name: reward.name,
      description: reward.description,
      rewardType: reward.rewardType.toString().trim(),
      personLimit: reward.personLimit.toString().trim(),
      limitCount: reward.limitCount.toString().trim(),
      value: 0,
      valueType: reward.valueType,
      remainingCount: reward.limitCount.toString().trim(),
      imageName: reward.imageName,
      imageType: reward.imageType,
      tags: reward.tags,
      validFrom: graphqlDateFormat(reward.validFrom),
      validTo: graphqlDateFormat(reward.validTo),
      launchDate: graphqlDateFormat(
        utils.defaultIfEmpty(reward.launchDate, reward.validFrom)
      ),
      isActive: 1,
      cost: reward.cost.toString().trim(),
      costType: reward.costType,
      ownerID: reward.ownerID,
      approverType: reward.approverType,
      location: reward.location,
      ownerType: reward.ownerType,
      createdBy: global.me.id,
      orgID: global.me.orgID,
      isDeleted: false,
      utid: reward.utid,
      tangoAccountID:
        reward.tangoAccountID !== '' ? reward.tangoAccountID : 'NA',
    };

    try {
      await API.graphql(graphqlOperation(createReward, { input: ourReward }));
      toast.success('Reward Created');
      return true;
    } catch (err) {
      LOG.error('createReward(): error creating Reward:' + err);
      toast.error('There was an error creating your reward');
      return false;
    }
  }

  async function doSubmit(values: any, actions: any) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    const ret = await createRewardRecord(values);
    if (ret) {
      navigate('/v2/performance/rewards');
    }
    actions.setSubmitting(false);
  }

  async function handleImageUpload(
    file: File,
    setFieldValue: (field: string, value: any) => void
  ) {
    try {
      const name = file.name.replace(/^.*[\\/]/, '');
      await Storage.put(name, file, { contentType: file.type });

      const VIDEO_EXTENSIONS = [
        'mov',
        'avi',
        'mp4',
        'mpeg',
        'ogv',
        'ts',
        '3gp',
        '3g2',
      ];
      const ext = name.split('.').pop()?.toLowerCase() || '';

      if (VIDEO_EXTENSIONS.includes(ext)) {
        setFieldValue('imageType', 'video');
      } else {
        setFieldValue('imageType', 'picture');
      }
      setFieldValue('imageName', name);

      const url = await Storage.get(name);
      setImagePreviewUrl(url as string);
    } catch (err) {
      LOG.error('Image upload error: ' + err);
      toast.error('Failed to upload image');
    }
  }

  useEffect(() => {
    if (rewardId) {
      getRewardForScreen(rewardId);
    }
  }, [rewardId]);

  let ownerList = [
    { label: 'Anyone can purchase', value: 'public' },
    {
      label:
        'Only ' +
        global.appSettings.labels.circle.singular +
        ' members can purchase',
      value: 'circle',
    },
    {
      label: 'Attach to a ' + global.appSettings.labels.mission.singular,
      value: 'mission',
    },
  ];

  if (!global.me.isAdmin) {
    ownerList = [
      {
        label:
          'Only ' +
          global.appSettings.labels.circle.singular +
          ' members can purchase',
        value: 'circle',
      },
      {
        label: 'Attach to a ' + global.appSettings.labels.mission.singular,
        value: 'mission',
      },
    ];
  }

  const approverList = [
    { label: 'No approval required', value: 'auto' },
    {
      label: global.appSettings.labels.reward.proper + ' owner',
      value: 'owner',
    },
    {
      label: global.appSettings.labels.circle.proper + ' owner',
      value: 'circle_owner',
    },
    {
      label: global.appSettings.labels.circle.proper + ' moderator',
      value: 'moderator',
    },
  ];

  return (
    <div className="h-full w-full flex flex-col overflow-hidden">
      <PageHeader
        title={isEditMode ? 'Edit Reward' : 'New Reward'}
        avatar={{
          src: global.me?.imageName || undefined,
          name: global.me?.name,
        }}
      />

      <ConfirmModal
        open={openCancelDialog}
        onClose={() => setOpenCancelDialog(false)}
        onConfirm={() => navigate('/v2/performance/rewards')}
        title="Cancel?"
        description={`Are you sure you wish to cancel the reward ${
          isEditMode ? 'editing' : 'creation'
        } process?`}
        confirmLabel="Yes, cancel"
        cancelLabel="Keep editing"
        variant="warning"
      />

      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Back / Cancel */}
        <button
          onClick={() => setOpenCancelDialog(true)}
          className="flex items-center gap-2 text-sm font-bold text-foreground hover:text-primary transition-colors mb-6"
        >
          <ArrowLeft className="size-4" />
          Cancel
        </button>

        <div className="max-w-[500px] mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-6">
            {isEditMode ? 'Edit Reward' : 'New Reward'}
          </h2>

          <Formik
            key={JSON.stringify(initialValues)}
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={createRewardValidationSchema()}
            onSubmit={(values, actions) => doSubmit(values, actions)}
          >
            {(formikProps) => (
              <Form className="space-y-6">
                {/* Image Upload */}
                <div className="w-full h-[300px] rounded-xl border-2 border-dashed border-border overflow-hidden">
                  {formikProps.values.imageName !== '' && imagePreviewUrl ? (
                    <div className="relative w-full h-full flex items-center justify-center bg-muted/30">
                      <img
                        src={imagePreviewUrl}
                        alt="Reward"
                        className="max-w-full max-h-full object-contain"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          formikProps.setFieldValue('imageName', '');
                          formikProps.setFieldValue('imageType', '');
                          setImagePreviewUrl(null);
                        }}
                        className="absolute top-2 right-2 size-6 rounded-full bg-background border border-border flex items-center justify-center text-muted-foreground hover:text-foreground"
                      >
                        &times;
                      </button>
                    </div>
                  ) : (
                    <label className="w-full h-full flex flex-col items-center justify-center gap-2 cursor-pointer bg-primary/5 hover:bg-primary/10 transition-colors">
                      <img
                        src="/images/image-icon.svg"
                        className="size-5 opacity-60"
                        alt=""
                      />
                      <span className="text-sm font-semibold text-primary">
                        Add Image
                      </span>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload(file, formikProps.setFieldValue);
                          }
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>

                {/* About Section */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/50">
                    <span className="text-sm font-medium text-foreground">
                      About
                    </span>
                  </div>
                  <div className="p-4 space-y-4">
                    <FormField
                      label="Name"
                      error={formikProps.errors.name as string}
                    >
                      <Input
                        placeholder="E.g. Apple Watch"
                        value={formikProps.values.name || ''}
                        onChange={(e) =>
                          formikProps.setFieldValue('name', e.target.value)
                        }
                      />
                    </FormField>
                    <FormField
                      label="Description (optional)"
                      error={formikProps.errors.description as string}
                    >
                      <textarea
                        placeholder="E.g. Apple watch 2021 in 5 different colors."
                        value={formikProps.values.description || ''}
                        onChange={(e) =>
                          formikProps.setFieldValue(
                            'description',
                            e.target.value
                          )
                        }
                        className="flex min-h-[80px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm placeholder:text-muted-foreground/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
                      />
                    </FormField>
                  </div>
                </div>

                {/* Settings Section */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/50">
                    <span className="text-sm font-medium text-foreground">
                      Settings
                    </span>
                  </div>
                  <div className="p-4 space-y-4">
                    <FormField
                      label="Cost"
                      error={formikProps.errors.cost as string}
                    >
                      <Input
                        placeholder="E.g. 500"
                        value={formikProps.values.cost || ''}
                        onChange={(e) =>
                          formikProps.setFieldValue('cost', e.target.value)
                        }
                      />
                    </FormField>

                    {/* Limit Count */}
                    <FormField
                      label="How many are Available?"
                      error={formikProps.errors.limitCount as string}
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="E.g. 10"
                          value={
                            formikProps.values.limitCount === '999999'
                              ? 'Unlimited'
                              : formikProps.values.limitCount || ''
                          }
                          onChange={(e) =>
                            formikProps.setFieldValue(
                              'limitCount',
                              e.target.value
                            )
                          }
                          disabled={formikProps.values.limitCount === '999999'}
                          className={cn(
                            formikProps.values.limitCount === '999999' &&
                              'text-primary'
                          )}
                        />
                        <Button
                          type="button"
                          variant={
                            formikProps.values.limitCount === '999999'
                              ? 'default'
                              : 'outline'
                          }
                          size="sm"
                          onClick={() => {
                            if (formikProps.values.limitCount === '999999') {
                              formikProps.setFieldValue('limitCount', '');
                            } else {
                              formikProps.setFieldValue('limitCount', '999999');
                            }
                          }}
                        >
                          Unlimited
                        </Button>
                      </div>
                    </FormField>

                    {/* Person Limit */}
                    <FormField
                      label="How many can each person buy?"
                      error={formikProps.errors.personLimit as string}
                    >
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="E.g. 2"
                          value={
                            formikProps.values.personLimit === '999999'
                              ? 'Unlimited'
                              : formikProps.values.personLimit || ''
                          }
                          onChange={(e) =>
                            formikProps.setFieldValue(
                              'personLimit',
                              e.target.value
                            )
                          }
                          disabled={formikProps.values.personLimit === '999999'}
                          className={cn(
                            formikProps.values.personLimit === '999999' &&
                              'text-primary'
                          )}
                        />
                        <Button
                          type="button"
                          variant={
                            formikProps.values.personLimit === '999999'
                              ? 'default'
                              : 'outline'
                          }
                          size="sm"
                          onClick={() => {
                            if (formikProps.values.personLimit === '999999') {
                              formikProps.setFieldValue('personLimit', '');
                            } else {
                              formikProps.setFieldValue(
                                'personLimit',
                                '999999'
                              );
                            }
                          }}
                        >
                          Unlimited
                        </Button>
                      </div>
                    </FormField>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        label="Start date"
                        error={formikProps.errors.validFrom as string}
                      >
                        <DateInput
                          value={
                            formikProps.values.validFrom
                              ? format(
                                  new Date(formikProps.values.validFrom),
                                  'yyyy-MM-dd'
                                )
                              : ''
                          }
                          onChange={(val) => {
                            if (val) {
                              formikProps.setFieldValue(
                                'validFrom',
                                format(new Date(val), 'MM/dd/yyyy')
                              );
                            }
                          }}
                        />
                      </FormField>

                      {formikProps.values.validTo === '01/01/2100' ? (
                        <FormField label="End date">
                          <Input value="Never" disabled />
                        </FormField>
                      ) : (
                        <FormField
                          label="End date"
                          error={formikProps.errors.validTo as string}
                        >
                          <DateInput
                            value={
                              formikProps.values.validTo
                                ? format(
                                    new Date(formikProps.values.validTo),
                                    'yyyy-MM-dd'
                                  )
                                : ''
                            }
                            onChange={(val) => {
                              if (val) {
                                formikProps.setFieldValue(
                                  'validTo',
                                  format(new Date(val), 'MM/dd/yyyy')
                                );
                              }
                            }}
                          />
                        </FormField>
                      )}
                    </div>

                    <div className="flex justify-end">
                      <Button
                        type="button"
                        variant={
                          formikProps.values.validTo === '01/01/2100'
                            ? 'default'
                            : 'outline'
                        }
                        size="sm"
                        onClick={() => {
                          if (formikProps.values.validTo === '01/01/2100') {
                            formikProps.setFieldValue(
                              'validTo',
                              format(
                                new Date(
                                  new Date().setDate(new Date().getDate() + 1)
                                ),
                                'MM/dd/yyyy'
                              )
                            );
                          } else {
                            formikProps.setFieldValue('validTo', '01/01/2100');
                          }
                        }}
                      >
                        No Expiration
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Permissions Section */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-muted/50">
                    <span className="text-sm font-medium text-foreground">
                      Permissions
                    </span>
                  </div>
                  <div className="p-4 space-y-4">
                    <FormField label="Who can purchase?">
                      <Select
                        value={formikProps.values.ownerType || ''}
                        onValueChange={(val) =>
                          formikProps.setFieldValue('ownerType', val)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ownerList.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>

                    <FormField label="Who approves reward purchases?">
                      <Select
                        value={formikProps.values.approverType || ''}
                        onValueChange={(val) =>
                          formikProps.setFieldValue('approverType', val)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {approverList.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormField>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-end pb-8">
                  <Button
                    type="submit"
                    disabled={!formikProps.isValid || formikProps.isSubmitting}
                    className="w-1/2 h-11 font-bold"
                  >
                    {formikProps.isSubmitting && (
                      <Loader2 className="size-4 animate-spin mr-2" />
                    )}
                    {formikProps.isSubmitting
                      ? 'Saving...'
                      : isEditMode
                      ? 'Update reward'
                      : 'Save changes'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
}
